"use client";

import { useCallback, useRef, useState } from "react";
import clsx from "clsx";
import { useRouter } from "next/navigation";
import { usePostHog } from "@posthog/next";
import { useTranslation } from "react-i18next";
import { RedesignButton } from "@/components/redesign/RedesignButton";
import { useLocalePath } from "@/hooks/useLocalePath";
import { isPersonalEmail, isPersonalDomain } from "@/utils/personalDomains";
import { DEMO_SOURCE_PARAM } from "@/utils/demoSource";
import {
  DEMO_CTA_CLICKED_EVENT,
  type DemoCtaPlacement,
} from "@/hooks/useDemoCtaTracking";

type MarketingHeroEmailCtaProps = {
  emailPlaceholder: string;
  ctaLabel: string;
  personalEmailWarning: string;
  continueAnywayLabel: string;
  onSubmit?: (email: string) => void;
  /** Identifies where the form was submitted from for analytics. */
  source?: string;
  /** Where this email CTA lives, for unified demo-CTA click tracking. */
  placement?: DemoCtaPlacement;
};

export function MarketingHeroEmailCta({
  emailPlaceholder,
  ctaLabel,
  personalEmailWarning,
  continueAnywayLabel,
  onSubmit,
  source = "homepage_hero",
  placement = "hero_email",
}: Readonly<MarketingHeroEmailCtaProps>) {
  const router = useRouter();
  const posthog = usePostHog();
  const lp = useLocalePath();
  const { i18n } = useTranslation();
  const demoPath = lp("/contact-sales");
  const [email, setEmail] = useState("");
  const [showPersonalWarning, setShowPersonalWarning] = useState(false);
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const submitEmail = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      const normalizedEmail = trimmed.toLowerCase();
      const domain = normalizedEmail.split("@")[1]?.toLowerCase() || null;

      posthog.identify(normalizedEmail, {
        email: normalizedEmail,
        email_domain: domain,
        lead_source: `marketing_${source}`,
      });
      posthog.capture("marketing_demo_email_submitted", {
        email: normalizedEmail,
        email_domain: domain,
        is_personal_email: !!domain && isPersonalDomain(domain),
        language: i18n.language,
        source,
      });
      posthog.capture(DEMO_CTA_CLICKED_EVENT, {
        source,
        placement,
        language: i18n.language,
      });

      if (onSubmit) {
        onSubmit(trimmed);
        return;
      }

      const params = new URLSearchParams({ email: trimmed });
      params.set(DEMO_SOURCE_PARAM, source);
      router.push(`${demoPath}?${params.toString()}`);
    },
    [demoPath, i18n.language, onSubmit, placement, posthog, router, source],
  );

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmed = email.trim();
    if (!trimmed.includes("@")) {
      setHasError(true);
      inputRef.current?.focus();
      return;
    }

    if (isPersonalEmail(trimmed) && !showPersonalWarning) {
      setShowPersonalWarning(true);
      return;
    }

    setShowPersonalWarning(false);
    submitEmail(trimmed);
  };

  return (
    <div className="w-full max-w-lg">
      <form onSubmit={handleSubmit}>
        <div
          className={clsx(
            "flex items-center gap-2 rounded-md border bg-white p-1.5 pl-4",
            hasError
              ? "border-red-300 animate-hero-shake"
              : "border-black/10",
          )}
        >
          <input
            ref={inputRef}
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (showPersonalWarning) setShowPersonalWarning(false);
              if (hasError) setHasError(false);
            }}
            placeholder={emailPlaceholder}
            className={clsx(
              "h-10 min-w-0 flex-1 bg-transparent",
              "font-monument text-base text-black placeholder:text-black/40",
              "focus:outline-none",
            )}
          />
          <RedesignButton
            htmlType="submit"
            className="h-10 shrink-0 px-4 py-0 text-base"
            iconAfter={
              <svg
                aria-hidden="true"
                className="size-4 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14" />
                <path d="m13 6 6 6-6 6" />
              </svg>
            }
          >
            {ctaLabel}
          </RedesignButton>
        </div>
      </form>

      {showPersonalWarning && (
        <div className="mt-3 rounded-lg bg-black/5 px-4 py-3 text-sm text-black/80">
          <p>{personalEmailWarning}</p>
          <button
            type="button"
            onClick={() => {
              setShowPersonalWarning(false);
              submitEmail(email.trim());
            }}
            className="mt-2 text-xs text-black/50 underline underline-offset-2 transition-colors hover:text-black"
          >
            {continueAnywayLabel}
          </button>
        </div>
      )}
    </div>
  );
}
